import { addToChainAfter, addToChainBefore, ApiHandlerDiscreteParameter, HasOnError, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod, Session, updateSession } from "@insite/client-framework/Services/SessionService";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";

import ApplicationStateDSG from "@DSG/Store/DSGState";
import formatDateWithTimezone from "@insite/client-framework/Common/Utilities/formatDateWithTimezone";
import {
    chain,
    PopulateApiParameter,
    SetCartStatus,
    SetCreditCard,
    UpdateCart,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import cloneDeep from "lodash/cloneDeep";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import { createShipTo, updateBillTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";



const convertDateToApiFormat = (date: Date | null) => (date ? formatDateWithTimezone(date) : "");
// this will enable HMR for custom handlers
if (module.hot) {
    module.hot.accept();
    const originalChain = chain.slice();
    module.hot.dispose(data => {
        chain.length = 0;
        originalChain.forEach(o => chain.push(o));
    });
}

interface PlaceOrderParameter {
    paymentMethod: string;
    poNumber: string;
    vatNumber?: string;
    saveCard: boolean;
    cardHolderName: string;
    cardNumber: string;
    cardType: string;
    expirationMonth: number;
    expirationYear: number;
    securityCode: string;
    useBillingAddress: boolean;
    address1: string;
    countryId: string;
    stateId: string;
    city: string;
    postalCode: string;
    onSuccess?: (cartId: string) => void;
    payPalToken?: string;
    payPalPayerId?: string;
    isPending?: boolean;
    isAdyenDropIn?: boolean;
    adyenPspReference?: string;
    accountHolderName?: string;
    accountNumber?: string;
    routingNumber?: string;
    payerId: string;
    maskedCardNumber: string;
}
type HandlerType = ApiHandlerDiscreteParameter<
    PlaceOrderParameter & HasOnError,
    UpdateCartApiParameter,
    CartResult,
    {
        cartToUpdate: Cart;
    }
>;

replaceInChain(chain, SetCartStatus, props => {
    const state = props.getState();
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const properties = "properties" in props.parameter ? props.parameter.properties : {};
    const {
        notes,
        customerOrderEmailAddress,
        customerOrderFirstName,
        customerOrderLastName,
        customerOrderPhoneNumber,
        isPickupSelected,
        jobName,
        pickupDetails,
        pickupTime,
        deliveryTime,
        oneTimeShipToId,
    } = properties as any;
    const isPickUp = state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp;
    const cartProperties = {
        ...cart?.properties,
        customerOrderFirstName: customerOrderFirstName,
        customerOrderEmailAddress: customerOrderEmailAddress,
        customerOrderLastName: customerOrderLastName,
        customerOrderPhoneNumber: customerOrderPhoneNumber,
        isPickupSelected: isPickUp ? "true" : "false",
        jobName,
        oneTimeShipToId:oneTimeShipToId,
        pickupDetails: isPickUp ? (pickupDetails && pickupDetails !== "" ? pickupDetails : "InsideBranch") : "",
    };

    if (!cart) {
        throw new Error("There was no current cart and we are trying to place the current cart as an order.");
    }

    const newStatus = cart?.requiresApproval
        ? "AwaitingApproval"
        : props.parameter?.isPending
        ? "PendingPaymentValidation"
            : "Submitted";

    props.cartToUpdate = {
        ...cloneDeep(cart),
        status: newStatus,
        poNumber: props.parameter.poNumber,
        customerVatNumber: props.parameter.vatNumber || "",
        notes,
        properties: cartProperties,
    };
});
replaceInChain(chain, PopulateApiParameter, props => {
    if (!props.cartToUpdate) {
        return;
    }

    const {
        pages: {
            checkoutReviewAndSubmit: { requestedDeliveryDate, requestedPickupDate },
        },
    } = props.getState();
    const state = props.getState() as ApplicationStateDSG;
    const pickupTime = state.onePageCheckoutStateExt.pickupTime;
    const deliveryTime = state.onePageCheckoutStateExt.deliveryTime;
   
    const tempDeliveryDate =
        typeof requestedDeliveryDate !== "undefined"
            ? requestedDeliveryDate
            : props.cartToUpdate.requestedDeliveryDateDisplay;
    const tempPickupDate =
        typeof requestedPickupDate !== "undefined"
            ? requestedPickupDate
            : props.cartToUpdate.requestedPickupDateDisplay;
    if (deliveryTime && tempDeliveryDate) {
        let is12HourClock = true;
        if (deliveryTime.split(" ")[1] === "PM") {
            is12HourClock = false;
        }

        tempDeliveryDate.setHours(
            !is12HourClock ? parseInt(deliveryTime.split(":")[0], 10) + 12 : parseInt(deliveryTime.split(":")[0], 10),
        );
        tempDeliveryDate.setMinutes(parseInt(deliveryTime.split(":")[1], 10));
    }
    if (pickupTime && tempPickupDate) {
        let is12HourClock = true;
        if (pickupTime.split(" ")[1] === "PM") {
            is12HourClock = false;
        }

        tempPickupDate.setHours(
            !is12HourClock ? parseInt(pickupTime.split(":")[0], 10) + 12 : parseInt(pickupTime.split(":")[0], 10),
        );
        tempPickupDate.setMinutes(parseInt(pickupTime.split(":")[1], 10));
    }
    props.apiParameter = {
        cart: {
            ...props.cartToUpdate,
            requestedDeliveryDate: convertDateToApiFormat(tempDeliveryDate).toLocaleString(),
            requestedPickupDate: convertDateToApiFormat(tempPickupDate).toLocaleString(),
        },
    };
});
const concatenateValues = (address2:any,ccFirstname:any,ccLastname:any,ccPhonenumber:any,ccEmailaddress:any,companyname:any) => {
    const values = [address2, ccFirstname, ccLastname, ccPhonenumber, ccEmailaddress, companyname];
    return values.join(' | ');
  };

replaceInChain(chain, SetCreditCard, props => {
    const { cartToUpdate } = props;
    const address2 = "address2" in props.parameter ? props.parameter.address2 : "";
    const ccFirstname = "ccFirstname" in props.parameter ? props.parameter.ccFirstname : "";
    const ccLastname = "ccLastname" in props.parameter ? props.parameter.ccLastname : "";
    const ccPhonenumber = "ccPhonenumber" in props.parameter ? props.parameter.ccPhonenumber : "";
    const ccEmailaddress = "ccEmailaddress" in props.parameter ? props.parameter.ccEmailaddress : "";
    const companyname = "companyname" in props.parameter ? props.parameter.companyname : "";
   
    if (cartToUpdate.paymentMethod?.isCreditCard && !props.parameter.isAdyenDropIn) {
        const updatedCreditCard = {
            ...cartToUpdate.paymentOptions!.creditCard!,
            cardHolderName: props.parameter.cardHolderName,
            cardNumber: props.parameter.cardNumber,
            cardType: props.parameter.cardType,
            expirationMonth: props.parameter.expirationMonth,
            expirationYear: props.parameter.expirationYear,
            securityCode: props.parameter.securityCode,
            useBillingAddress: props.parameter.useBillingAddress,
            maskedCardNumber: props.parameter.maskedCardNumber,
        };

        cartToUpdate.paymentOptions!.storePaymentProfile = props.parameter.saveCard;

        if (!props.parameter.useBillingAddress) {
            const countries = getCurrentCountries(props.getState());
            if (!countries) {
                throw new Error("The active countries for the current website are not available.");
            }

            const selectedCountry = countries.find(country => country.id === props.parameter.countryId);
            if (!selectedCountry) {
                throw new Error("The selected country for the card billing address is not valid.");
            }
            
            let customBillingAddressParameters = concatenateValues(address2, ccFirstname, ccLastname, ccPhonenumber, ccEmailaddress, companyname);

            updatedCreditCard.address1 = props.parameter.address1 + (customBillingAddressParameters && customBillingAddressParameters.length > 0 ? ` | ${customBillingAddressParameters}` : "");
            updatedCreditCard.country = selectedCountry.name;
            updatedCreditCard.countryAbbreviation = selectedCountry.abbreviation;

            if (!!selectedCountry.states && selectedCountry.states.length > 0) {
                const selectedState = selectedCountry.states?.find(state => state.id === props.parameter.stateId);
                if (!selectedState) {
                    throw new Error("The selected state for the card billing address is not valid.");
                }

                updatedCreditCard.state = selectedState.name;
                updatedCreditCard.stateAbbreviation = selectedState.abbreviation;
            }

            updatedCreditCard.city = props.parameter.city;
            updatedCreditCard.postalCode = props.parameter.postalCode;
            
        }

        cartToUpdate.paymentOptions!.creditCard = updatedCreditCard;
    }

    if (cartToUpdate.paymentMethod?.isCreditCard || cartToUpdate.paymentMethod?.isPaymentProfile) {
        const spreedlyFrame = getSpreedlyFrame();
        if (spreedlyFrame?.isSpreedly()) {
            cartToUpdate.paymentOptions!.creditCard!.browserInfo = spreedlyFrame.getBrowserInfo();
        }
    }
});

addToChainBefore(chain, UpdateCart, async props => {
    const { dispatch, getState, cartToUpdate } = props;
    const state = getState() as ApplicationStateDSG;
    const useOneTimeAddress = state.onePageCheckoutStateExt.useOneTimeAddress;

    const {
        pages: {
            onePageCheckout: { shippingAddressFormState },
        },
    } = state;

    if (!shippingAddressFormState) {
        return;
    }
    if(!useOneTimeAddress)
        {
            return;
        }

    const shipTo = getShipToState(state, cartToUpdate.shipToId).value;
    const billTo = getBillToState(state, cartToUpdate.billToId).value;
    if (!shipTo || !billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;
    let localModifiedShipTo1 = {...localModifiedShipTo,
        isNew:true}
    
    if (!areAddressesTheSame(shipTo, localModifiedShipTo1)) {
        var a  = localModifiedShipTo1.isNew
            ? await createShipTo({ shipTo: localModifiedShipTo1 })
            : await updateShipTo({ shipTo: localModifiedShipTo1, billToId: billTo.id });

        dispatch(loadShipTo({ shipToId: a.id, billToId: billTo.id }));

        props.apiParameter.cart.shipToId = a.id;
    }

   
    const { cartId } = getState().pages.onePageCheckout;

    if (!cartId && (billTo || shipTo)) {
        await updateSession({
            session: {
                billToId: cartToUpdate.billToId,
                shipToId: props.apiParameter.cart.shipToId ?? cartToUpdate.shipToId,
                customerWasUpdated: true,
            } as Session,
        });
    }
});
